// CancellationRequestsList.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TablePagination,
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    CardContent,
    Grid,
    TextField,
    InputAdornment,
    IconButton,
    Skeleton,
    Typography,
    Chip,
    Menu,
    MenuItem,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoIcon from '@mui/icons-material/Info';
import axiosServices from 'utils/axios';
import useAuth from 'hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import MainCard from './MainCard';
import Page from 'ui-component/Page';
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import PolicyIcon from '@mui/icons-material/Policy';
import { useDispatch } from 'react-redux';
import { EmployeeActions } from 'store/slices/employees';

export default function CancellationRequestsList() {
    const { user } = useAuth();
    const isAdmin = user?.roleName === 'ADMIN';
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { loadRequestsCount } = EmployeeActions;

    // — table state
    const [data, setData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // — dialog state
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [isCancelling, setIsCancelling] = useState(false);
    const [loading, setLoading] = useState(false);

    // — notes dialog
    const [notesDialogOpen, setNotesDialogOpen] = useState(false);
    const [notesText, setNotesText] = useState('');

    // — action menu
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);

    const openNotesDialog = (text) => {
        setNotesText(text);
        setNotesDialogOpen(true);
    };

    const handleMenuOpen = (event, row) => {
        setAnchorEl(event.currentTarget);
        setMenuRow(row);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuRow(null);
    };

    // redirect non-admins
    useEffect(() => {
        if (user && !isAdmin) navigate('/listing/manage-listing');
    }, [user, isAdmin, navigate]);

    // — fetcher
    const fetchRequests = useCallback(async () => {
        if (!isAdmin) return;
        setLoading(true);
        try {
            const { data: res } = await axiosServices.get('/user/cancellations-requests', {
                headers: { Authorization: `Bearer ${localStorage.getItem('serviceToken')}` },
                params: {
                    page: page + 1,
                    limit: rowsPerPage,
                    search: search || undefined
                }
            });
            setData(res.requests);
            setTotalCount(res.totalRequests);
        } catch (err) {
            console.error('Failed to load cancellation requests', err);
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage, search, isAdmin]);

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    // — handlers
    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPage(0);
    };
    const handleChangePage = (_, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };
    const handleStatusFilter = (e) => {
        setStatusFilter(e.target.value);
        setPage(0);
    };

    const handleConfirmCancel = async () => {
        setIsCancelling(true);
        try {
            await axiosServices.patch(
                '/user/cancel-policy',
                { user_id: selectedUserId },
                { headers: { Authorization: `Bearer ${localStorage.getItem('serviceToken')}` } }
            );
            await fetchRequests();
            dispatch(loadRequestsCount());
        } catch (err) {
            console.error(err);
        } finally {
            setIsCancelling(false);
            setCancelDialogOpen(false);
        }
    };

    // client-side filtering by status
    const filteredData = data.filter((row) => {
        if (statusFilter === 'approved') return !!row.cancelledAt;
        if (statusFilter === 'pending') return !row.cancelledAt;
        return true;
    });

    return (
        <Page title="Dashboard: Cancellation Requests">
            <Breadcrumbs heading="Clients" links={[{ name: 'Cancellation Requests', href: '/cancellation-requests' }]} />
            <MainCard title="Cancellation Requests" content={false}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search..."
                                value={search}
                                onChange={handleSearch}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select label="Status" value={statusFilter} onChange={handleStatusFilter}>
                                    <MenuItem value="all">All</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="approved">Approved</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </CardContent>

                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>Requested On</TableCell>
                                <TableCell>Reason</TableCell>
                                {/* <TableCell>Notes</TableCell> */}
                                <TableCell>Status</TableCell>
                                <TableCell align="center">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading
                                ? [...Array(rowsPerPage)].map((_, i) => (
                                      <TableRow key={i}>
                                          <TableCell colSpan={8}>
                                              <Skeleton variant="rectangular" height={32} />
                                          </TableCell>
                                      </TableRow>
                                  ))
                                : filteredData.map((row) => (
                                      <TableRow key={row.id}>
                                          <TableCell>{`${row.firstName || ''} ${row.lastName || ''}`}</TableCell>
                                          <TableCell>{row.email}</TableCell>
                                          <TableCell>{row.phoneNo || '-'}</TableCell>
                                          <TableCell>{new Date(row.cancellationRequestedAt).toLocaleDateString()}</TableCell>
                                          <TableCell>{row.cancellationReason || '-'}</TableCell>
                                          {/* <TableCell>
                                              {row.cancellationNotes ? (
                                                  <IconButton
                                                      color="info"
                                                      size="small"
                                                      onClick={() => openNotesDialog(row.cancellationNotes)}
                                                  >
                                                      <InfoIcon fontSize="small" />
                                                  </IconButton>
                                              ) : (
                                                  '-'
                                              )}
                                          </TableCell> */}
                                          <TableCell>
                                              <Chip
                                                  label={row.cancelledAt ? 'Approved' : 'Pending'}
                                                  color={row.cancelledAt ? 'success' : 'warning'}
                                                  size="small"
                                                  sx={{ color: 'white' }}
                                              />
                                          </TableCell>
                                          <TableCell align="center">
                                              <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
                                                  <MoreVertIcon fontSize="small" />
                                              </IconButton>
                                          </TableCell>
                                      </TableRow>
                                  ))}
                            {!loading && filteredData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography variant="h4" sx={{ p: 2 }}>
                                            No requests found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box display="flex" justifyContent="flex-end" p={1}>
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </Box>

                {/* Action dropdown menu */}
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                    {/* <MenuItem
                        disabled={!menuRow?.cancellationReason}
                        onClick={() => {
                            alert(menuRow?.cancellationReason || 'No reason provided');
                            handleMenuClose();
                        }}
                    >
                        View Reason
                    </MenuItem> */}
                    <MenuItem
                        disabled={!menuRow?.cancellationNotes}
                        onClick={() => {
                            openNotesDialog(menuRow.cancellationNotes);
                            handleMenuClose();
                        }}
                    >
                        <InfoIcon color="info" sx={{ fontSize: '1.2rem', mr: 1 }} />
                        View Notes
                    </MenuItem>
                    <MenuItem
                        disabled={!!menuRow?.cancelledAt}
                        onClick={() => {
                            setSelectedUserId(menuRow.id);
                            setCancelDialogOpen(true);
                            handleMenuClose();
                        }}
                    >
                        <PolicyIcon color="error" sx={{ fontSize: '1.2rem', mr: 1 }} />
                        Cancel Services
                    </MenuItem>
                </Menu>

                {/* Notes Dialog */}
                <Dialog open={notesDialogOpen} onClose={() => setNotesDialogOpen(false)} aria-labelledby="notes-dialog-title">
                    <DialogTitle id="notes-dialog-title">Cancellation Notes</DialogTitle>
                    <DialogContent sx={{ width: 400 }} dividers>
                        <DialogContentText>{notesText}</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setNotesDialogOpen(false)}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Confirm Cancellation Dialog */}
                <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
                    <DialogTitle>Confirm Services Cancellation</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Cancelling services will deactivate this user’s account and all associated data.
                            <strong> Are you sure you want to proceed?</strong>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <LoadingButton loading={isCancelling} color="error" onClick={handleConfirmCancel} autoFocus>
                            Yes, Cancel Services
                        </LoadingButton>
                        <Button onClick={() => setCancelDialogOpen(false)}>No, Go Back</Button>
                    </DialogActions>
                </Dialog>
            </MainCard>
        </Page>
    );
}
